/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import type {
  HomepageBlockResponse,
  Platform,
} from '../models/homepageBlockTypes';
import { HomepageService } from '../services/homepageApiService';
import { FallbackHomepageApiService } from '../services/fallbackHomepageApiService';
import fallbackHomepageResponse from '../assets/data/fallbackHomepageResponse.json';

const QUERY_PERSIST_CACHE = 'persist_cache';
// Keep the last good homepage around so slow starts can render cached content
// first, while manual refresh still fetches the latest data.
const STALE_TIME = 30 * 60 * 1000;
const GC_TIME = 60 * 60 * 1000;

export const homepageKeys = {
  // Each platform gets its own cache slot so ios, android, mobile, web-mobile,
  // and web never share a cached response.
  data: (
    platform: Platform,
    locale: string,
    geoId: string,
    currency?: string,
  ) =>
    [
      QUERY_PERSIST_CACHE,
      'homepage',
      'data',
      platform,
      locale,
      geoId,
      currency,
    ] as const,
};

export const createHomepageBlocksQueryOptions = (
  api: HomepageService,
  fallbackApi: FallbackHomepageApiService,
  platform: Platform,
  locale: string,
  geoId: string,
  currency?: string,
) =>
  queryOptions<HomepageBlockResponse>({
    queryKey: homepageKeys.data(platform, locale, geoId, currency),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      try {
        return await api.getHomePage(
          platform,
          'homepage',
          'cosmos',
          locale,
          geoId,
          currency,
        );
      } catch (error) {
        console.error(
          `[home][homepage] Primary homepage API failed, currency - ${currency}, geoId - ${geoId}, locale - ${locale}:`,
          error,
        );

        try {
          const data = await fallbackApi.getFallbackHomePage(platform, locale);
          return { ...data, _isFallback: true };
        } catch (fallbackError) {
          console.error(
            '[home][homepage] Fallback homepage API failed:',
            fallbackError,
          );

          return {
            ...(fallbackHomepageResponse as unknown as HomepageBlockResponse),
            _isFallback: true,
          };
        }
      }
    },
    refetchOnMount: true,
    staleTime: (query) => (query.state.data?._isFallback ? 0 : STALE_TIME),
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    retry: 2,
  });
